import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import { useFilters, useSortBy, usePagination } from "react-table";
import {
  getTurnos,
  deleteTurno,
  getTurnoById,
} from "../helpers/rutaTurnos";
import ModalFormTurno from "../components/ModalFormTurno";
import ModalTurnosEnEspera from "../components/ModalTurnosEnEspera";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

const Styles = styled.div`
  .table-container {
    overflow: auto;
    height: 600px;
  }

  table {
    border: 1px solid black;
    border-spacing: 0;
    font-size: 13px;
  }

  th,
  td {
    padding: 0.5rem;
    border-bottom: 1px solid black;
    border-right: 1px solid black;
  }

  th {
    background-color: #778899;
    position: sticky;
    top: 0;
    z-index: 2;
    text-align: center;
    font-size: 12px;
  }

  td {
    background-color: white;
  }

  .pagination {
    padding: 0.5rem;
    background-color: #778899;
    font-size: 15px;
    font-weight: bold;
  }
`;

/* ---------- utils ---------- */
function toStartOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toEndOfDay(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function norm(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

const formatFecha = (value) => {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    return `${d}/${m}/${y}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const atencionDesc = (v) => {
  switch (Number(v)) {
    case 0:
      return "SIN DEFINIR";
    case 1:
      return "EN ESPERA";
    case 2:
      return "ATENDIDO";
    case 3:
      return "RECHAZADO";
    default:
      return "";
  }
};

const getPersonaTxt = (p) => {
  if (!p) return "";
  if (isObj(p)) {
    return [p.codper, p.nombre].filter(Boolean).join(" - ");
  }
  return String(p || "");
};

const getUsuarioActual = () => {
  try {
    return JSON.parse(localStorage.getItem("usuario")) || {};
  } catch {
    return {};
  }
};

const getPersonaIdFromUsuario = (usuario) => {
  return (
    usuario?.persona?._id ||
    usuario?.persona?.id ||
    usuario?.persona ||
    usuario?.personal?._id ||
    usuario?.personal?.id ||
    usuario?.personal ||
    ""
  );
};

/* =======================================================
   Componente
======================================================= */
export default function AppTurnoReactTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [turnoEdit, setTurnoEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [showEnEspera, setShowEnEspera] = useState(false);
  const [refreshEsperaKey, setRefreshEsperaKey] = useState(0);

  const [filtros, setFiltros] = useState({
    desde: "",
    hasta: "",
    atencion: "",
    persona: "",
    nombre: "",
    telefono: "",
    observacion: "",
  });

  const usuarioActual = useMemo(() => getUsuarioActual(), []);
  const personaId = useMemo(
    () => getPersonaIdFromUsuario(usuarioActual),
    [usuarioActual]
  );

  console.log("usuarioActual", usuarioActual);
  console.log("personaId", personaId);  

  const setF = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }));

  /* ---------- Fetch ---------- */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await getTurnos();

      const list = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.turnos)
        ? resp.turnos
        : [];

      setData(list);
    } catch (err) {
      console.error("❌ Error al obtener turnos:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------- F6: modal en espera ---------- */
  const abrirModalEnEspera = useCallback(() => {
    setRefreshEsperaKey((k) => k + 1);
    setShowEnEspera(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "F6") {
        e.preventDefault();
        abrirModalEnEspera();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [abrirModalEnEspera]);

  /* ---------- Acciones ---------- */
  const handleClose = () => {
    setShowModal(false);
    setTurnoEdit(null);
    fetchData();
    setRefreshEsperaKey((k) => k + 1);
  };

  const handleCloseEnEspera = () => {
    setShowEnEspera(false);
    fetchData();
  };

  const borrar = useCallback(
    async (mongoId) => {
      if (!window.confirm("¿Eliminar turno?")) return;

      try {
        await deleteTurno(mongoId);
        setData((prev) => prev.filter((t) => t._id !== mongoId));
        fetchData();
        setRefreshEsperaKey((k) => k + 1);
      } catch (err) {
        console.error("❌ Error al eliminar turno:", err);
      }
    },
    [fetchData]
  );

  const abrirEdicion = useCallback(async (id) => {
    try {
      const resp = await getTurnoById(id);
      const det = resp?.turno ?? resp;
      setTurnoEdit(det);
      setShowModal(true);
    } catch (err) {
      console.error("❌ Error getTurnoById:", err);
    }
  }, []);

  /* ---------- Filtro en memoria ---------- */
  const dataFiltrada = useMemo(() => {
    const dDesde = toStartOfDay(filtros.desde);
    const dHasta = toEndOfDay(filtros.hasta);

    return (data || []).filter((r) => {
      if (!(r?.activo ?? true)) return false;

      const f = r?.fechaTurno ? new Date(r.fechaTurno) : null;
      if (dDesde && (!f || f < dDesde)) return false;
      if (dHasta && (!f || f > dHasta)) return false;

      if (
        filtros.atencion &&
        !norm(atencionDesc(r?.atencion)).includes(norm(filtros.atencion)) &&
        !String(r?.atencion ?? "").includes(String(filtros.atencion))
      ) {
        return false;
      }

      if (filtros.persona) {
        const personaTxt = getPersonaTxt(r?.persona);
        if (!norm(personaTxt).includes(norm(filtros.persona))) return false;
      }

      if (filtros.nombre && !norm(r?.nombre).includes(norm(filtros.nombre))) {
        return false;
      }

      if (
        filtros.telefono &&
        !norm(r?.telefono).includes(norm(filtros.telefono))
      ) {
        return false;
      }

      if (
        filtros.observacion &&
        !norm(r?.observacion).includes(norm(filtros.observacion))
      ) {
        return false;
      }

      return true;
    });
  }, [data, filtros]);

  const limpiarFiltros = () =>
    setFiltros({
      desde: "",
      hasta: "",
      atencion: "",
      persona: "",
      nombre: "",
      telefono: "",
      observacion: "",
    });

  const quickHoy = () => {
    const hoy = new Date();
    const y = hoy.getFullYear();
    const m = String(hoy.getMonth() + 1).padStart(2, "0");
    const d = String(hoy.getDate()).padStart(2, "0");
    const iso = `${y}-${m}-${d}`;
    setF("desde", iso);
    setF("hasta", iso);
  };

  const quickUltimos7 = () => {
    const hoy = new Date();
    const d7 = new Date(hoy);
    d7.setDate(hoy.getDate() - 6);

    const toISO = (dt) =>
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
        dt.getDate()
      ).padStart(2, "0")}`;

    setF("desde", toISO(d7));
    setF("hasta", toISO(hoy));
  };

  /* ---------- Columnas ---------- */
  const columns = useMemo(
    () => [
      {
        id: "editar",
        Header: "Edita",
        accessor: "_id",
        width: 60,
        Cell: ({ row }) => (
          <button
            className="action-btn action-btn--edit"
            title="Editar"
            onClick={() => abrirEdicion(row.original._id)}
          >
            <i className="fa fa-pencil" aria-hidden="true" />
          </button>
        ),
      },
      {
        id: "eliminar",
        Header: "🗑️",
        accessor: "_id",
        width: 60,
        Cell: ({ row }) => (
          <button
            className="action-btn action-btn--del"
            title="Eliminar"
            onClick={() => borrar(row.original._id)}
          >
            <i className="fa fa-trash-o" aria-hidden="true" />
          </button>
        ),
      },
      { Header: "Nro", accessor: "codtur", width: 70 },
      {
        Header: "Fecha",
        accessor: (row) => (row.fechaTurno ? new Date(row.fechaTurno) : null),
        id: "fechaTurno",
        width: 120,
        Cell: ({ value }) =>
          value instanceof Date && !Number.isNaN(value.getTime())
            ? formatFecha(value)
            : "",
      },
      {
        Header: "Persona",
        accessor: (row) => getPersonaTxt(row?.persona),
        id: "persona",
        width: 220,
      },
      {
        Header: "Atención",
        accessor: (row) => atencionDesc(row?.atencion),
        id: "atencion",
        width: 150,
      },
      { Header: "Nombre", accessor: "nombre", width: 220 },
      { Header: "Teléfono", accessor: "telefono", width: 140 },
      { Header: "Observación", accessor: "observacion", width: 320 },
    ],
    [abrirEdicion, borrar]
  );

  return (
    <div className="container mt-4">
      <h3 className="text-center mb-2">LISTADO DE TURNOS</h3>

      {/* Filtros */}
      <div className="container mb-2">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Fecha desde
            </label>
            <input
              type="date"
              className="form-control"
              value={filtros.desde}
              onChange={(e) => setF("desde", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Fecha hasta
            </label>
            <input
              type="date"
              className="form-control"
              value={filtros.hasta}
              onChange={(e) => setF("hasta", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Atención
            </label>
            <input
              className="form-control"
              placeholder="espera / atendido"
              value={filtros.atencion}
              onChange={(e) => setF("atencion", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Persona
            </label>
            <input
              className="form-control"
              value={filtros.persona}
              onChange={(e) => setF("persona", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Nombre
            </label>
            <input
              className="form-control"
              value={filtros.nombre}
              onChange={(e) => setF("nombre", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-2 d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm flex-fill"
              onClick={quickHoy}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}
            >
              Hoy
            </button>
            <button
              className="btn btn-outline-success btn-sm flex-fill"
              onClick={quickUltimos7}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}
            >
              7 días
            </button>
            <button
              className="btn btn-outline-primary btn-sm flex-fill"
              onClick={limpiarFiltros}
              style={{ fontSize: 16, lineHeight: 1.1, padding: "8px 12px" }}
            >
              Limpiar
            </button>
          </div>
        </div>

        <div className="row g-2 align-items-end mt-2">
          <div className="col-12 col-md-6 col-lg-3">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Teléfono
            </label>
            <input
              className="form-control"
              value={filtros.telefono}
              onChange={(e) => setF("telefono", e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-9">
            <label className="form-label" style={{ fontSize: "1.5rem" }}>
              Observación
            </label>
            <input
              className="form-control"
              value={filtros.observacion}
              onChange={(e) => setF("observacion", e.target.value)}
            />
          </div>
        </div>
      </div>

      <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={dataFiltrada}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {turnoEdit && (
        <Modal show={showModal} onHide={handleClose} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Turno</Modal.Title>
          </Modal.Header>

          <ModalFormTurno turno={turnoEdit} handleClose={handleClose} />
        </Modal>
      )}

      <ModalTurnosEnEspera
        show={showEnEspera}
        onHide={handleCloseEnEspera}
        personaId={personaId}
        refreshKey={refreshEsperaKey}
      />
    </div>
  );
}